// Broker New Deal Submission — advisory per-section AI feedback shown in the
// wizard after the broker clicks Evaluate & Feedback. Mirrors the Collection
// upload FeedbackPanel: coaching, never blocking. The feedback is persisted
// as a JSON string on the section state; we parse it defensively (the stored
// blob uses the server's default PascalCase property names).

import type {
  DealSubmissionAiFeedbackIssue,
  DealSubmissionAiFeedbackResult,
  DealSubmissionAiFeedbackVerdict,
} from "../../api/types";
import { PortalMarkdown } from "../../components/PortalMarkdown";

/** Read a property from a parsed object case-insensitively. */
function pick(obj: Record<string, unknown>, ...names: string[]): unknown {
  for (const name of names) {
    if (name in obj) return obj[name];
    const lower = name.toLowerCase();
    const key = Object.keys(obj).find((k) => k.toLowerCase() === lower);
    if (key) return obj[key];
  }
  return undefined;
}

/**
 * Parse the persisted feedback JSON into a typed result. Returns null on
 * empty / malformed input so the caller simply renders nothing.
 */
export function parseSectionFeedback(
  json: string | null,
): DealSubmissionAiFeedbackResult | null {
  if (!json) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  return normalizeFeedback(parsed);
}

/**
 * Normalize an already-parsed feedback object (e.g. the result of an
 * Evaluate & Feedback call) into the typed shape, tolerant of PascalCase /
 * camelCase. Returns null for non-object input so the caller renders nothing.
 */
export function normalizeFeedback(
  parsed: unknown,
): DealSubmissionAiFeedbackResult | null {
  if (!parsed || typeof parsed !== "object") return null;
  const obj = parsed as Record<string, unknown>;

  const summary = String(pick(obj, "summary") ?? "");
  const available = Boolean(pick(obj, "available"));

  const issuesRaw = pick(obj, "issues");
  const issues: DealSubmissionAiFeedbackIssue[] = Array.isArray(issuesRaw)
    ? issuesRaw.map((i) => {
        const io = (i ?? {}) as Record<string, unknown>;
        return {
          title: String(pick(io, "title") ?? ""),
          suggestion: String(pick(io, "suggestion") ?? ""),
        };
      })
    : [];

  // Reserve the "couldn't review" heading (Unavailable) for genuine failures.
  // Map the verdict tolerantly — the stored blob may carry the enum as a
  // string OR as an integer (1/2/3) — and when it's missing/unrecognised but
  // the response carries review content, treat it as success rather than
  // defaulting to the error case.
  const mapped = mapVerdict(pick(obj, "verdict"));
  const hasContent = summary.trim().length > 0 || issues.length > 0;
  const verdict: DealSubmissionAiFeedbackVerdict =
    mapped ?? (hasContent ? "NeedsAttention" : "Unavailable");

  return { verdict, summary, issues, available };
}

/**
 * Map a raw verdict value to a known verdict, tolerating both the string form
 * ("LooksGood" / "NeedsAttention" / "Unavailable") and the enum's numeric form
 * (1 / 2 / 3) the persisted blob may use. Returns null for anything else so the
 * caller can fall back on content presence.
 */
function mapVerdict(raw: unknown): DealSubmissionAiFeedbackVerdict | null {
  const s = (typeof raw === "number" ? String(raw) : String(raw ?? "")).toLowerCase();
  if (s === "looksgood" || s === "1") return "LooksGood";
  if (s === "needsattention" || s === "2") return "NeedsAttention";
  if (s === "unavailable" || s === "3") return "Unavailable";
  return null;
}

export function SectionFeedbackPanel({
  feedback,
  context = "section",
}: {
  feedback: DealSubmissionAiFeedbackResult;
  context?: "section" | "document";
}) {
  const tone =
    feedback.verdict === "LooksGood"
      ? "bg-status-success/10 border-status-success"
      : feedback.verdict === "NeedsAttention"
        ? "bg-status-warning/10 border-status-warning"
        : "bg-white-warm border-border-light";

  const heading =
    feedback.verdict === "LooksGood"
      ? "Looks good!"
      : feedback.verdict === "NeedsAttention"
        ? "A couple of things to check"
        : context === "document"
          ? "We couldn't review this document right now"
          : "We couldn't review this section right now";

  return (
    <div
      className={`p-space-3 border rounded-md ${tone}`}
      data-testid="section-feedback"
    >
      <p className="text-small font-semibold text-onyx m-0">{heading}</p>
      {feedback.summary && (
        <div className="mt-space-2">
          {/* The model often returns Markdown (headings, bold, lists); render
              it formatted so raw "###"/"**" never leak into the body. */}
          <PortalMarkdown markdown={feedback.summary} />
        </div>
      )}
      {feedback.issues.length > 0 && (
        <ul className="mt-space-2 mb-0 pl-space-4 flex flex-col gap-space-1">
          {feedback.issues.map((issue, idx) => (
            <li key={idx} className="text-small text-onyx">
              <span className="font-semibold">{issue.title}</span>
              {issue.title && issue.suggestion ? " — " : ""}
              {issue.suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
